import { type FC, useState } from "react";

import { CourseSelectionList } from "./CourseSelectionList";
import { MoveCoursesConfirmationModal } from "./MoveCoursesConfirmationModal";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { Trans, t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { CourseWithGroupingType } from "store/selectors/types";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

type AddCourseGroupingModalProps = {
  courseCodes: CourseWithGroupingType[];
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  groupingsWithCodes: Array<{ order: number }>;
  onClose: () => void;
};

export const AddCourseGroupingModal: FC<AddCourseGroupingModalProps> = ({
  courseCodes,
  eventName,
  eventProperties,
  groupingsWithCodes,
  onClose,
}) => {
  const [name, setName] = useState("");
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { setQueryParams } = useQueryParamsManager();

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    // Check if any selected course codes belong to a different grouping
    const coursesFromOtherGroupings = courseCodes.filter(
      (code) => selectedCourseIds.includes(code.id) && code.grouping_id !== null,
    );

    if (coursesFromOtherGroupings.length > 0) {
      // Show confirmation modal
      setShowConfirmation(true);
      return;
    }

    // No course codes from other groupings, proceed directly
    handleCreateGrouping();
  };

  const handleCreateGrouping = async () => {
    const newOrder =
      groupingsWithCodes.length > 0
        ? Math.max(...groupingsWithCodes.map((g) => g.order || 0)) + 1
        : 0;

    const newGrouping = await storeApi.course_groupings.create({
      course_ids: selectedCourseIds,
      order: newOrder,
      parent_id: null,
      title: name.trim(),
    });

    toastSuccessMessage(t("home.toasts.grouping_created"));
    setQueryParams({
      [COURSE_GROUPING_ID_QUERY_PARAM]: newGrouping.id,
      [COURSE_ID_QUERY_PARAM]: null,
    });
    onClose();
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
  };

  const handleConfirmationConfirm = async () => {
    await handleCreateGrouping();
  };

  const toggleCourseChecked = (courseId: string) => {
    const newSelectedIds = [...selectedCourseIds];

    const codeIndex = newSelectedIds.indexOf(courseId);
    if (codeIndex > -1) {
      newSelectedIds.splice(codeIndex, 1);
    } else {
      newSelectedIds.push(courseId);
    }

    setSelectedCourseIds(newSelectedIds);
  };

  if (showConfirmation) {
    const coursesFromOtherGroupings = courseCodes.filter(
      (code) => selectedCourseIds.includes(code.id) && code.grouping_id !== null,
    );

    return (
      <MoveCoursesConfirmationModal
        courses={coursesFromOtherGroupings}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
      />
    );
  }

  return (
    <div className="mt-8 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("nav_list.new_title_course_grouping")}</div>

          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.course_grouping_name")}
            value={name}
            isEditing
            onChange={(e) => setName(String(e.target.value))}
          />
        </label>
      </div>

      {courseCodes.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="font-medium">
            <Trans i18nKey="home.courses.add_course_grouping_title">
              Add course codes to Folder
              <span className="font-normal text-black-tint-55">(optional)</span>
            </Trans>
          </div>

          <CourseSelectionList
            courses={courseCodes}
            selectedCourseIds={selectedCourseIds}
            onCourseToggle={toggleCourseChecked}
          />
        </div>
      )}

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!name.trim()}
          kind="blue"
          size="s"
          title={t("common.add.course_grouping")}
          onClick={handleSubmit}
        >
          {t("common.add.course_grouping")}
        </Button>
      </div>
    </div>
  );
};
